import BaseView from "./base-view";
import ModelLoader from "../model-loader";
import Signal from "../control-flow/signal";
import assets from "../assets";
import pc from "../parametric-cache";
import { BufferGeometry, Material, Mesh, Object3D } from "three";
import { DrawerBox } from "../domain";
import { setOrigin } from "../view3d/set-origin";

/**
 * A drawer box is either a parametric box, generated by ParametricCache's
 * DrawerBox extension, or it's a model loaded from the modelAssetId property.
 */
export class DrawerBoxView extends BaseView {

    declare model: DrawerBox;

    root: Object3D;

    slide: Object3D;

    onSlideAvailable = new Signal<[Object3D]>('views', 'onSlideAvialable')
    onBoxAvailable = new Signal<[Object3D]>('views', 'onBoxAvailable');

    constructor(model: DrawerBox) {
        super(model);
        this.onReady.name = 'DrawerBoxView';
        this.init();
    }

    make() {
        if(this.model.modelAssetId) {
            // the root object will be created later
        } else {
            this.root = new Mesh(new BufferGeometry(), new Material());
            this.root.castShadow = this.root.receiveShadow = true;
            this.add(this.root);
        }
    }

    bind() {
        this.model.bind('width', this.changeSize)
        this.model.on('height', this.changeSize)
        this.model.on('depth', this.changeSize)
        if(this.model.modelAssetId) {
            // nothing to do here, the material binding must happen in the
            // changeSize implementation when the model has loaded
        } else {
            const root = this.root as Mesh;
            this.bindMaterial(root, this.model.materials[0])
        }
    }

    changeSize = async () => {
        if(this.model.modelAssetId) {
            const
                ml = ModelLoader.getInstance(),
                modelUrl = assets.resolve(this.model.modelAssetId, 'LowResModel'),
                slideAssetId = assets.resolve(this.model.modelAssetId, 'DrawerSlideModel'),
                slideUrl = assets.resolve(slideAssetId, 'LowResModel');
            this.updates.queue(
                () => Promise.all([ml.get(modelUrl), ml.get(slideUrl)]),
                ([main, slide]) => {
                    this.root?.removeFromParent()
                    // We add the slide to the loaded GLB under the assumption
                    // that it has the same origin, initially.
                    main.add(slide);
                    setOrigin(main, 0.5, 0.5, 1);
                    this.root = main;
                    this.add(main);

                    this.slide = slide;
                    this.onSlideAvailable.trigger(slide);

                    this.bindMaterialTree(main, this.model.materials);
                    this.onReady.trigger();
                    this.onBoxAvailable.trigger(this.root);
                });
        } else {
            this.updates.queue(
                () => pc.make(
                    'DrawerBox',
                    this.model.width,
                    this.model.height,
                    this.model.depth,
                    this.model.front,
                ),
                (boxGeo) => {
                    const root = this.root as Mesh;
                    root.geometry = boxGeo;
                    this.onReady.trigger();
                    this.onBoxAvailable.trigger(this.root);
                }
            )
        }
    }
}
export default DrawerBoxView;
